import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton,
  Chip, Tooltip, TextField, Box, CircularProgress, Alert, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl, InputLabel,
  Select, MenuItem, Grid, Snackbar, Card, CardContent
} from '@mui/material';
import {
  Delete as DeleteIcon, Search as SearchIcon, Refresh as RefreshIcon,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon, Receipt as ReceiptIcon,
  Download as DownloadIcon, Edit as EditIcon
} from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { usePermissions } from '../hooks/usePermissions';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const FRAIS_INSCRIPTION = 3000;

const extractId = (v) => {
  if (!v) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v).trim();
  return String(v._id || v.id || v);
};

const compareIds = (a, b) => extractId(a) === extractId(b);

const Paiements = () => {
  const { user } = usePermissions();
  // Lecture seule pour CONSULTANT et DIRECTION_GENERALE : pas d'ajout, de
  // modification de prix/bourse ni de suppression de versement.
  const peutModifierPaiements = ['ADMIN', 'DIRECTEUR', 'DIRECTION_FINANCIERE'].includes(user?.role);

  const [etudiants, setEtudiants] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [personnel, setPersonnel] = useState([]);

  const [annees, setAnnees] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [inscriptions, setInscriptions] = useState([]);

  const [filters, setFilters] = useState({ annee_id: '', filiere_id: '', niveau_id: '', search: '', statut: '' });

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editEtudiant, setEditEtudiant] = useState(null);
  const [editForm, setEditForm] = useState({ montantTotal: 38000, pourcentageBourse: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selectedEtudiant, setSelectedEtudiant] = useState(null);
  const [openVersementDialog, setOpenVersementDialog] = useState(false);
  const [versementForm, setVersementForm] = useState({ montant: '', type: 'VERSEMENT', date: new Date().toISOString().split('T')[0], employe: '' });

  const [openExcessDialog, setOpenExcessDialog] = useState(false);
  const [excessData, setExcessData] = useState(null);

  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [detailEtudiant, setDetailEtudiant] = useState(null);

  const [openRecuDialog, setOpenRecuDialog] = useState(false);
  const [recuEtudiant, setRecuEtudiant] = useState(null);

  const showNotification = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadReferences = useCallback(async () => {
    try {
      const [anneesRes, filieresRes, niveauxRes, inscriptionsRes, personnelRes] = await Promise.all([
        axios.get(`${API_URL}/annees`),
        axios.get(`${API_URL}/filieres`),
        axios.get(`${API_URL}/niveaux`),
        axios.get(`${API_URL}/inscriptions`),
        axios.get(`${API_URL}/personnel`).catch(() => ({ data: { data: [] } }))
      ]);
      setAnnees(anneesRes.data?.data || anneesRes.data || []);
      setFilieres(filieresRes.data?.data || filieresRes.data || []);
      setNiveaux(niveauxRes.data?.data || niveauxRes.data || []);
      setInscriptions(inscriptionsRes.data?.data || inscriptionsRes.data || []);
      setPersonnel(personnelRes.data?.data || personnelRes.data || []);
    } catch (err) {
      console.error('Erreur chargement références:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [etudiantsRes, paiementsRes] = await Promise.all([
        axios.get(`${API_URL}/etudiants?limit=100000`),
        axios.get(`${API_URL}/paiements`)
      ]);
      const etuData = etudiantsRes.data?.data || etudiantsRes.data || [];
      setEtudiants(Array.isArray(etuData) ? etuData : []);
      setPaiements(paiementsRes.data?.data || paiementsRes.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReferences(); loadData(); }, []);

  // Filtres cascade : options des select
  // Filiere.annee_id peut stocker soit l'ObjectId MongoDB, soit la chaîne personnalisée (ex: "1", "2")
  const filieresFiltrees = filieres.filter(f => {
    if (!filters.annee_id) return true;
    const annee = annees.find(a => compareIds(a._id, filters.annee_id));
    if (!annee) return false;
    return compareIds(f.annee_id, annee._id) || compareIds(f.annee_id, annee.annee_id);
  });
  // Les niveaux sont liés aux filières via filiere.niveaux[].niveau_id (pas via un champ filiere_id sur Niveau)
  const niveauxFiltres = niveaux.filter(n => {
    if (filters.filiere_id) {
      const filiere = filieresFiltrees.find(f => compareIds(f._id, filters.filiere_id));
      if (!filiere || !filiere.niveaux) return false;
      return filiere.niveaux.some(fn => compareIds(fn.niveau_id, n.niveau_id));
    }
    if (filters.annee_id) {
      return filieresFiltrees.some(f => f.niveaux && f.niveaux.some(fn => compareIds(fn.niveau_id, n.niveau_id)));
    }
    return true;
  });

  const getInscriptionsByEtudiant = (etudiantId) => {
    return inscriptions.filter(ins => {
      const insId = ins.etudiant?._id || ins.etudiant_id;
      return compareIds(insId, etudiantId);
    });
  };

  const filteredEtudiants = etudiants.filter(e => {
    const eId = extractId(e._id || e.id);
    const etuInscriptions = getInscriptionsByEtudiant(eId);

    if (filters.annee_id) {
      const match = etuInscriptions.some(ins => {
        const insAnnee = ins.anneeUniversitaire?._id || ins.annee_universitaire_id;
        return compareIds(insAnnee, filters.annee_id);
      });
      if (!match) return false;
    }

    if (filters.filiere_id) {
      const match = etuInscriptions.some(ins => {
        const insFiliere = ins.filiere?._id || ins.filiere_id;
        return compareIds(insFiliere, filters.filiere_id);
      });
      if (!match) return false;
    }

    if (filters.niveau_id) {
      const match = etuInscriptions.some(ins => {
        const insNiveau = ins.niveau?._id || ins.niveau_id;
        return compareIds(insNiveau, filters.niveau_id);
      });
      if (!match) return false;
    }

    if (filters.search) {
      const s = filters.search.toLowerCase();
      if (!`${e.Nom} ${e.Prénom} ${e.CNE}`.toLowerCase().includes(s)) return false;
    }
    if (filters.statut === 'impayes') {
      const p = getPaiement(eId);
      const totalNet = getMontantNet(eId);
      const verse = p?.montantVerse || 0;
      if (Math.max(0, totalNet - verse) === 0) return false;
    } else if (filters.statut === 'payes') {
      const p = getPaiement(eId);
      const totalNet = getMontantNet(eId);
      const verse = p?.montantVerse || 0;
      if (Math.max(0, totalNet - verse) > 0) return false;
    }
    return true;
  });

  const getPaiement = (etudiantId) => {
    return paiements.find(p => compareIds(p.etudiant?._id || p.etudiant, etudiantId));
  };

  const getMontantNet = (etudiantId) => {
    const base = getMontantTotal(etudiantId);
    const etudiant = etudiants.find(e => compareIds(e._id || e.id, etudiantId));
    const bourse = etudiant?.pourcentage_bourse || 0;
    return base - (base - FRAIS_INSCRIPTION) * (bourse / 100);
  };

  const getBourse = (etudiantId) => {
    const etudiant = etudiants.find(e => compareIds(e._id || e.id, etudiantId));
    return etudiant?.pourcentage_bourse || 0;
  };

  // Détermine l'année (1 à 5) de l'étudiant à partir de l'intitulé de son niveau
  const getAnneeNiveau = (intitule) => {
    const s = (intitule || '').toUpperCase();
    if (!s) return 0;
    if (/1(ÈRE|ERE| ère| ere)/.test(s) || /\bL1\b/.test(s) || /IRM1/.test(s) || /MASI1/.test(s) || /1CPI/.test(s) || /CPI 1/.test(s)) return 1;
    if (/2(ÈME|EME| ème| eme)/.test(s) || /\bL2\b/.test(s) || /IRM2/.test(s) || /MASI2/.test(s) || /CPI2/.test(s) || /CPI 2/.test(s)) return 2;
    if (/3(ÈME|EME| ème| eme)/.test(s) || /\bL3\b/.test(s) || /\b3GI\b/.test(s) || /\b3GC\b/.test(s)) return 3;
    if (/4(ÈME|EME| ème| eme)/.test(s) || /\bM1\b/.test(s) || /\b4GI\b/.test(s) || /\b4GC\b/.test(s)) return 4;
    if (/5(ÈME|EME| ème| eme)/.test(s) || /\bM2\b/.test(s) || /\b5GI\b/.test(s) || /\b5GC\b/.test(s) || /MASTER 2/.test(s)) return 5;
    return 0;
  };

  const getNiveauIntitule = (etudiantId) => {
    const inscriptionsEtudiant = getInscriptionsByEtudiant(etudiantId);
    for (const ins of inscriptionsEtudiant) {
      const intitule = ins.niveau?.intitule || ins.niveau?.code;
      if (intitule) return intitule;
      const niv = niveaux.find(n => compareIds(n._id || n.niveau_id, ins.niveau_id || ins.niveau?._id));
      if (niv) return niv.intitule || niv.code;
    }
    return '';
  };

  // Montant à payer selon le niveau : 1-3ème année = 38000 DH, 4-5ème année = 42000 DH
  const getMontantTotal = (etudiantId) => {
    const annee = getAnneeNiveau(getNiveauIntitule(etudiantId));
    return annee >= 4 ? 42000 : 38000;
  };

  const stats = {
    nbEtudiants: filteredEtudiants.length,
    totalAPayer: filteredEtudiants.reduce((sum, e) => sum + getMontantNet(extractId(e._id || e.id)), 0),
    totalVerse: filteredEtudiants.reduce((sum, e) => sum + (getPaiement(extractId(e._id || e.id))?.montantVerse || 0), 0),
    totalRestant: filteredEtudiants.reduce((sum, e) => {
      const eId = extractId(e._id || e.id);
      const p = getPaiement(eId);
      const totalNet = getMontantNet(eId);
      const verse = p?.montantVerse || 0;
      return sum + Math.max(0, totalNet - verse);
    }, 0)
  };

  const doAddVersement = async () => {
    const eId = selectedEtudiant._id || selectedEtudiant.id;
    let p = getPaiement(extractId(eId));
    if (!p) {
      const createRes = await axios.post(`${API_URL}/paiements`, { etudiantId: eId, montantTotal: getMontantTotal(extractId(eId)) });
      p = createRes.data?.data || createRes.data;
    }
    await axios.put(`${API_URL}/paiements/${p._id}/versement`, {
      montant: Number(versementForm.montant),
      type: versementForm.type,
      date: versementForm.date,
      employe: versementForm.employe
    });
    const res = await axios.get(`${API_URL}/paiements`);
    setPaiements(res.data?.data || res.data || []);
    showNotification('Versement ajouté');
    setOpenVersementDialog(false);
    setVersementForm({ montant: '', type: 'VERSEMENT', date: new Date().toISOString().split('T')[0], employe: '' });
  };

  const handleAddVersement = async () => {
    if (!versementForm.montant || versementForm.montant <= 0) {
      showNotification('Montant invalide', 'error');
      return;
    }
    const eId = selectedEtudiant._id || selectedEtudiant.id;
    const totalNet = getMontantNet(extractId(eId));
    const p = getPaiement(extractId(eId));
    const verseActuel = p?.montantVerse || 0;
    const nouveauVerse = verseActuel + Number(versementForm.montant);
    if (nouveauVerse > totalNet) {
      setExcessData({
        nom: `${selectedEtudiant.Nom} ${selectedEtudiant.Prénom}`,
        totalNet,
        verseActuel,
        montantSaisi: Number(versementForm.montant),
        nouveauVerse,
        exces: nouveauVerse - totalNet
      });
      setOpenExcessDialog(true);
      return;
    }
    try { await doAddVersement(); }
    catch (err) { showNotification(err.response?.data?.message || 'Erreur', 'error'); }
  };

  const handleConfirmExcess = async () => {
    setOpenExcessDialog(false);
    setExcessData(null);
    try { await doAddVersement(); }
    catch (err) { showNotification(err.response?.data?.message || 'Erreur', 'error'); }
  };

  const handleSaveEdit = async () => {
    if (!editEtudiant) return;
    const eId = editEtudiant._id || editEtudiant.id;
    try {
      setLoading(true);
      if (editForm.pourcentageBourse !== (editEtudiant.pourcentage_bourse || 0)) {
        await axios.put(`${API_URL}/etudiants/${eId}`, { pourcentage_bourse: Number(editForm.pourcentageBourse) });
      }
      if (editForm.montantTotal !== getMontantTotal(eId)) {
        const p = getPaiement(eId);
        if (p) {
          await axios.put(`${API_URL}/paiements/${p._id}`, { montantTotal: Number(editForm.montantTotal) });
        } else {
          await axios.post(`${API_URL}/paiements`, { etudiantId: eId, montantTotal: Number(editForm.montantTotal) });
        }
      }
      await Promise.all([loadData(), loadReferences()]);
      showNotification('Étudiant mis à jour');
      setOpenEditDialog(false);
      setEditEtudiant(null);
    } catch (err) {
      showNotification(err.response?.data?.message || 'Erreur', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const data = filteredEtudiants.map(e => {
      const eId = extractId(e._id || e.id);
      const totalNet = getMontantNet(eId);
      const verse = getPaiement(eId)?.montantVerse || 0;
      const reste = Math.max(0, totalNet - verse);
      return {
        Nom: e.Nom, Prénom: e.Prénom, CNE: e.CNE || '',
        Bourse: `${e.pourcentage_bourse || 0}%`,
        'Net (DH)': totalNet, 'Versé (DH)': verse, 'Reste (DH)': reste
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Paiements');
    const cols = [
      { wch: 20 }, { wch: 20 }, { wch: 14 },
      { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }
    ];
    ws['!cols'] = cols;
    XLSX.writeFile(wb, `paiements_${new Date().toISOString().slice(0, 10)}.xlsx`);
    showNotification(`Exporté ${filteredEtudiants.length} étudiant(s)`);
  };

  const handleDeleteVersement = async (paiementId, versementId) => {
    try {
      await axios.delete(`${API_URL}/paiements/${paiementId}/versement/${versementId}`);
      const res = await axios.get(`${API_URL}/paiements`);
      setPaiements(res.data?.data || res.data || []);
      showNotification('Versement supprimé');
    } catch (err) {
      showNotification('Erreur', 'error');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5">Gestion des Paiements</Typography>
          <Box display="flex" gap={1}>
            <Button variant="outlined" startIcon={<DownloadIcon />}
              onClick={handleExport} disabled={loading || filteredEtudiants.length === 0}>
              Exporter
            </Button>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
              Actualiser
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth size="small" placeholder="Rechercher..."
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Année</InputLabel>
              <Select value={filters.annee_id} label="Année"
                onChange={e => setFilters(f => ({ ...f, annee_id: e.target.value, filiere_id: '', niveau_id: '' }))}>
                <MenuItem value="">Toutes</MenuItem>
                {annees.map(a => <MenuItem key={a._id} value={a._id}>{a.intitule}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4}>
            <FormControl fullWidth size="small" disabled={!filters.annee_id}>
              <InputLabel>Filière</InputLabel>
              <Select value={filters.filiere_id} label="Filière"
                onChange={e => setFilters(f => ({ ...f, filiere_id: e.target.value, niveau_id: '' }))}>
                <MenuItem value="">Toutes</MenuItem>
                {filieresFiltrees.map(f =>
                  <MenuItem key={f._id} value={f._id}>{f.code_filiere} - {f.intitule}</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={6}>
            <FormControl fullWidth size="small" disabled={!filters.filiere_id}>
              <InputLabel>Niveau</InputLabel>
              <Select value={filters.niveau_id} label="Niveau"
                onChange={e => setFilters(f => ({ ...f, niveau_id: e.target.value }))}>
                <MenuItem value="">Tous</MenuItem>
                {niveauxFiltres.map(n =>
                  <MenuItem key={n._id} value={n._id}>{n.code} - {n.intitule}</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select value={filters.statut} label="Statut"
                onChange={e => setFilters(f => ({ ...f, statut: e.target.value }))}>
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="impayes">Impayés (reste > 0)</MenuItem>
                <MenuItem value="payes">Payés</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Étudiant</TableCell>
                <TableCell>CNE</TableCell>
                <TableCell align="center">Bourse</TableCell>
                <TableCell align="right">Net (DH)</TableCell>
                <TableCell align="right">Versé (DH)</TableCell>
                <TableCell align="right">Reste (DH)</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center"><CircularProgress /></TableCell></TableRow>
              ) : filteredEtudiants.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary">Aucun étudiant trouvé</Typography></TableCell></TableRow>
              ) : (
                filteredEtudiants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(e => {
                  const eId = extractId(e._id || e.id);
                  const p = getPaiement(eId);
                  const totalNet = getMontantNet(eId);
                  const bourse = getBourse(eId);
                  const verse = p?.montantVerse || 0;
                  const reste = Math.max(0, totalNet - verse);
                  return (
                    <TableRow key={eId} hover>
                      <TableCell><Typography variant="body2" fontWeight="medium">{e.Nom} {e.Prénom}</Typography></TableCell>
                      <TableCell>{e.CNE}</TableCell>
                      <TableCell align="center">{bourse > 0 ? <Chip label={`${bourse}%`} size="small" color="warning" /> : '-'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totalNet.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: '#10b981', fontWeight: 'bold' }}>{verse.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ color: reste > 0 ? '#ef4444' : '#10b981', fontWeight: 'bold' }}>{reste.toLocaleString()}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {peutModifierPaiements && (
                        <Tooltip title="Ajouter un versement">
                          <IconButton size="small" color="primary"
                            onClick={() => { setSelectedEtudiant(e); setOpenVersementDialog(true); }}>
                            <PaymentIcon />
                          </IconButton>
                        </Tooltip>
                        )}
                        {peutModifierPaiements && (
                        <Tooltip title="Modifier prix / bourse">
                          <IconButton size="small" color="warning"
                            onClick={() => {
                              setEditEtudiant(e);
                              setEditForm({ montantTotal: getMontantTotal(eId), pourcentageBourse: bourse });
                              setOpenEditDialog(true);
                            }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        )}
                        <Tooltip title="Reçu">
                          <IconButton size="small" color="success"
                            onClick={() => {
                              setRecuEtudiant({ ...e, paiement: p, inscriptions: getInscriptionsByEtudiant(eId) });
                              setOpenRecuDialog(true);
                            }}>
                            <ReceiptIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Détails">
                          <IconButton size="small" color="default"
                            onClick={() => {
                              setDetailEtudiant({ ...e, paiement: p });
                              setOpenDetailDialog(true);
                            }}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filteredEtudiants.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]} labelRowsPerPage="Lignes par page"
        />
      </Paper>

      <Dialog open={openVersementDialog} onClose={() => setOpenVersementDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Ajouter un versement - {selectedEtudiant?.Nom} {selectedEtudiant?.Prénom}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField fullWidth label="Montant (DH)" type="number" value={versementForm.montant}
                onChange={e => setVersementForm(f => ({ ...f, montant: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={versementForm.type} label="Type"
                  onChange={e => setVersementForm(f => ({ ...f, type: e.target.value }))}>
                  <MenuItem value="VERSEMENT">Versement</MenuItem>
                  <MenuItem value="SUR_PLACE">Sur place</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Date" type="date" value={versementForm.date}
                onChange={e => setVersementForm(f => ({ ...f, date: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Personnel</InputLabel>
                <Select value={versementForm.employe} label="Personnel"
                  onChange={e => setVersementForm(f => ({ ...f, employe: e.target.value }))}>
                  <MenuItem value="">-- Aucun --</MenuItem>
                  {personnel.map(p => (
                    <MenuItem key={p._id || p.id} value={`${p.nom || p.Nom} ${p.prenom || p.Prénom}`}>
                      {p.nom || p.Nom} {p.prenom || p.Prénom} {p.fonction ? `(${p.fonction})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVersementDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleAddVersement}>Ajouter</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={() => { setOpenEditDialog(false); setEditEtudiant(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Modifier - {editEtudiant?.Nom} {editEtudiant?.Prénom}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField fullWidth label="Montant total (DH)" type="number" disabled
                value={editForm.montantTotal}
                helperText="Montant fixé selon le niveau (1ère-3ème : 38 000 DH, 4ème-5ème : 42 000 DH)" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Pourcentage bourse (%)" type="number"
                value={editForm.pourcentageBourse}
                onChange={e => setEditForm(f => ({ ...f, pourcentageBourse: Number(e.target.value) }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenEditDialog(false); setEditEtudiant(null); }}>Annuler</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openExcessDialog} onClose={() => { setOpenExcessDialog(false); setExcessData(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#ef4444' }}>
          ⚠ Dépassement du montant à payer
        </DialogTitle>
        <DialogContent>
          {excessData && (
            <Box>
              <Typography variant="body1" fontWeight="bold" mb={1}>{excessData.nom}</Typography>
              <Typography variant="body2" mb={1}>
                Le total versé ({excessData.nouveauVerse.toLocaleString()} DH) dépassera le montant net à payer ({excessData.totalNet.toLocaleString()} DH).
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow><TableCell>Montant net à payer</TableCell><TableCell align="right" sx={{ fontWeight: 'bold' }}>{excessData.totalNet.toLocaleString()} DH</TableCell></TableRow>
                  <TableRow><TableCell>Déjà versé</TableCell><TableCell align="right">{excessData.verseActuel.toLocaleString()} DH</TableCell></TableRow>
                  <TableRow><TableCell>Nouveau versement</TableCell><TableCell align="right" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>{excessData.montantSaisi.toLocaleString()} DH</TableCell></TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', color: '#ef4444' }}>Dépassement</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ef4444' }}>+{excessData.exces.toLocaleString()} DH</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenExcessDialog(false); setExcessData(null); }}>Annuler</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmExcess}>
            Ajouter quand même
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDetailDialog} onClose={() => setOpenDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Détails des paiements - {detailEtudiant?.Nom} {detailEtudiant?.Prénom}
        </DialogTitle>
        <DialogContent>
          {detailEtudiant && (
            <>
              <Box display="flex" gap={4} mb={3} flexWrap="wrap">
                {(() => {
                  const dtl = detailEtudiant.paiement;
                  const dBase = getMontantTotal(extractId(detailEtudiant._id || detailEtudiant.id));
                  const dBourse = getBourse(extractId(detailEtudiant._id || detailEtudiant.id));
                  const dNet = dBase - (dBase - FRAIS_INSCRIPTION) * (dBourse / 100);
                  const dVerse = dtl?.montantVerse || 0;
                  const dReste = Math.max(0, dNet - dVerse);
                  return (
                    <>
                      <Box><Typography variant="body2" color="text.secondary">Base</Typography><Typography variant="h6">{dBase.toLocaleString()} DH</Typography></Box>
                      <Box><Typography variant="body2" color="text.secondary">Frais d'inscription</Typography><Typography variant="h6">-{FRAIS_INSCRIPTION.toLocaleString()} DH</Typography></Box>
                      {dBourse > 0 && <Box><Typography variant="body2" color="text.secondary">Bourse</Typography><Typography variant="h6" color="#f59e0b">{dBourse}%</Typography></Box>}
                      <Box><Typography variant="body2" color="text.secondary">Net</Typography><Typography variant="h6" color="#6366f1">{dNet.toLocaleString()} DH</Typography></Box>
                      <Box><Typography variant="body2" color="text.secondary">Versé</Typography><Typography variant="h6" color="#10b981">{dVerse.toLocaleString()} DH</Typography></Box>
                      <Box><Typography variant="body2" color="text.secondary">Reste</Typography><Typography variant="h6" color="#ef4444">{dReste.toLocaleString()} DH</Typography></Box>
                    </>
                  );
                })()}
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Montant</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Employé</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!detailEtudiant.paiement?.versements || detailEtudiant.paiement.versements.length === 0) ? (
                    <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary">Aucun versement</Typography></TableCell></TableRow>
                  ) : (
                    detailEtudiant.paiement.versements.map((v, i) => (
                      <TableRow key={v._id || i}>
                        <TableCell>{new Date(v.date).toLocaleDateString('fr-FR')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{v.montant.toLocaleString()} DH</TableCell>
                        <TableCell><Chip label={v.type === 'VERSEMENT' ? 'Versement' : 'Sur place'} size="small" color={v.type === 'VERSEMENT' ? 'primary' : 'warning'} /></TableCell>
                        <TableCell>{v.employe || '-'}</TableCell>
                        <TableCell align="center">
                          {peutModifierPaiements && (
                          <IconButton size="small" color="error"
                            onClick={() => {
                              handleDeleteVersement(detailEtudiant.paiement._id, v._id);
                              setDetailEtudiant(prev => ({ ...prev }));
                            }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openRecuDialog} onClose={() => setOpenRecuDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon color="primary" />
            Reçu de Paiement
          </Box>
        </DialogTitle>
        <DialogContent id="receipt-content">
          {recuEtudiant && (() => {
            const e = recuEtudiant;
            const p = e.paiement;
            const ins = e.inscriptions?.[0];
            const filiereStr = ins?.filiere?.intitule || ins?.filiere?.code_filiere || '-';
            const niveauStr = ins?.niveau?.intitule || ins?.niveau?.code || '-';
            const anneeStr = ins?.anneeUniversitaire?.intitule || '-';
            const base = getMontantTotal(extractId(e._id || e.id));
            const bourse = getBourse(extractId(e._id || e.id));
            const net = base - (base - FRAIS_INSCRIPTION) * (bourse / 100);
            const verse = p?.montantVerse || 0;
            const reste = Math.max(0, net - verse);
            const versements = p?.versements || [];
            return (
              <Box id="receipt-print-area">
                <Box textAlign="center" mb={3} pb={2} borderBottom="2px solid #1976d2">
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    Établissement Supérieur
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Reçu de Paiement
                  </Typography>
                </Box>

                <Grid container spacing={1} mb={3}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Étudiant</Typography>
                    <Typography variant="body1" fontWeight="bold">{e.Nom} {e.Prénom}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">CNE</Typography>
                    <Typography variant="body1" fontWeight="bold">{e.CNE}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Filière</Typography>
                    <Typography variant="body1">{filiereStr}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Niveau</Typography>
                    <Typography variant="body1">{niveauStr}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Année</Typography>
                    <Typography variant="body1">{anneeStr}</Typography>
                  </Grid>
                </Grid>

                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Montant (DH)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Responsable</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versements.length === 0 ? (
                      <TableRow><TableCell colSpan={4} align="center"><Typography color="text.secondary">Aucun versement</Typography></TableCell></TableRow>
                    ) : (
                      versements.map((v, i) => (
                        <TableRow key={v._id || i}>
                          <TableCell>{new Date(v.date).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{v.montant.toLocaleString()} DH</TableCell>
                          <TableCell>
                            <Chip label={v.type === 'VERSEMENT' ? 'Versement' : 'Sur place'} size="small"
                              color={v.type === 'VERSEMENT' ? 'primary' : 'warning'} />
                          </TableCell>
                          <TableCell>{v.employe || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                <Box textAlign="right" mt={2} pt={2} borderTop="1px solid #e0e0e0">
                  <Typography variant="body1">
                    <strong>Base :</strong> {base.toLocaleString()} DH
                  </Typography>
                  <Typography variant="body1">
                    <strong>Frais d'inscription :</strong> -{FRAIS_INSCRIPTION.toLocaleString()} DH
                  </Typography>
                  {bourse > 0 && (
                    <Typography variant="body1" color="#f59e0b">
                      <strong>Bourse :</strong> {bourse}%
                    </Typography>
                  )}
                  <Typography variant="body1" color="#6366f1">
                    <strong>Net à payer :</strong> {net.toLocaleString()} DH
                  </Typography>
                  <Typography variant="body1" color="#10b981">
                    <strong>Total versé :</strong> {verse.toLocaleString()} DH
                  </Typography>
                  <Typography variant="body1" color={reste > 0 ? '#ef4444' : '#10b981'}>
                    <strong>Reste :</strong> {reste.toLocaleString()} DH
                  </Typography>
                </Box>

                <Box textAlign="center" mt={4}>
                  <Typography variant="body2" color="text.secondary">
                    Reçu généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRecuDialog(false)}>Fermer</Button>
          <Button variant="contained" startIcon={<ReceiptIcon />}
            onClick={() => {
              const e = recuEtudiant;
              if (!e) return;
              const p = e.paiement;
              const ins = e.inscriptions?.[0];
              const filiereStr = ins?.filiere?.intitule || ins?.filiere?.code_filiere || '-';
              const niveauStr = ins?.niveau?.intitule || ins?.niveau?.code || '-';
              const anneeStr = ins?.anneeUniversitaire?.intitule || '-';
              const base = getMontantTotal(extractId(e._id || e.id));
              const bourse = getBourse(extractId(e._id || e.id));
              const net = base - (base - FRAIS_INSCRIPTION) * (bourse / 100);
              const verse = p?.montantVerse || 0;
              const reste = Math.max(0, net - verse);
              const versements = p?.versements || [];
              const versementsRows = versements.length === 0
                ? '<tr><td colspan="4" style="text-align:center;color:#999">Aucun versement</td></tr>'
                : versements.map(v =>
                    `<tr>
                      <td>${new Date(v.date).toLocaleDateString('fr-FR')}</td>
                      <td style="font-weight:bold">${v.montant.toLocaleString()} DH</td>
                      <td>${v.type === 'VERSEMENT' ? 'Versement' : 'Sur place'}</td>
                      <td>${v.employe || '-'}</td>
                    </tr>`
                  ).join('');
              const today = new Date().toLocaleDateString('fr-FR');
              const time = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
              const bourseRow = bourse > 0 ? `<p style="color:#f59e0b"><strong>Bourse :</strong> ${bourse}%</p>` : '';
              const fraisRow = `<p><strong>Frais d'inscription :</strong> -${FRAIS_INSCRIPTION.toLocaleString()} DH</p>`;
              const printWin = window.open('', '_blank', 'width=800,height=600');
              printWin.document.write(`<!DOCTYPE html>
<html><head><title>Reçu de Paiement</title>
<style>
  body { font-family: 'Segoe UI', Arial, sans-serif; padding: 50px; color: #333; margin:0; }
  .header { text-align:center; margin-bottom:30px; padding-bottom:15px; border-bottom:3px solid #1976d2; }
  .header h1 { color:#1976d2; margin:0; font-size:22px; }
  .header p { color:#666; margin:5px 0 0; font-size:14px; }
  .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:4px 20px; margin-bottom:25px; }
  .info-grid .label { color:#666; font-size:12px; }
  .info-grid .value { font-weight:bold; font-size:14px; margin-bottom:8px; }
  table { width:100%; border-collapse:collapse; margin-bottom:20px; }
  th { background:#f5f5f5; font-weight:bold; padding:10px 12px; text-align:left; border-bottom:2px solid #ddd; font-size:13px; }
  td { padding:10px 12px; text-align:left; border-bottom:1px solid #e0e0e0; font-size:13px; }
  .totals { text-align:right; margin-top:15px; padding-top:15px; border-top:2px solid #e0e0e0; }
  .totals p { margin:4px 0; font-size:14px; }
  .footer { text-align:center; margin-top:40px; font-size:12px; color:#999; }
</style></head><body>
  <div class="header">
    <h1>Établissement Supérieur</h1>
    <p>Reçu de Paiement</p>
  </div>
  <div class="info-grid">
    <div><div class="label">Étudiant</div><div class="value">${e.Nom} ${e.Prénom}</div></div>
    <div><div class="label">CNE</div><div class="value">${e.CNE}</div></div>
    <div><div class="label">Filière</div><div class="value">${filiereStr}</div></div>
    <div><div class="label">Année</div><div class="value">${anneeStr}</div></div>
    <div><div class="label">Niveau</div><div class="value">${niveauStr}</div></div>
    <div></div>
  </div>
  <table>
    <thead><tr><th>Date</th><th>Montant</th><th>Type</th><th>Responsable</th></tr></thead>
    <tbody>${versementsRows}</tbody>
  </table>
  <div class="totals">
    <p><strong>Base :</strong> ${base.toLocaleString()} DH</p>
    ${fraisRow}
    ${bourseRow}
    <p style="color:#6366f1"><strong>Net à payer :</strong> ${net.toLocaleString()} DH</p>
    <p style="color:#10b981"><strong>Total versé :</strong> ${verse.toLocaleString()} DH</p>
    <p style="color:${reste > 0 ? '#ef4444' : '#10b981'}"><strong>Reste :</strong> ${reste.toLocaleString()} DH</p>
  </div>
  <div class="footer">Reçu généré le ${today} à ${time}</div>
</body></html>`);
              printWin.document.close();
              printWin.print();
            }}>
            Imprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Paiements;


